import React from 'react';
import { Card, CardContent } from '../components/ui/card';
import { Target, Users, Award, TrendingUp } from 'lucide-react';

const About = () => {
  const values = [
    {
      icon: <Target className="w-10 h-10 text-[#FFC107]" />,
      title: 'Our Mission',
      description: 'To make sustainable solar energy accessible and affordable for every home and business in India'
    },
    {
      icon: <Users className="w-10 h-10 text-[#4CAF50]" />,
      title: 'Expert Team',
      description: 'Certified engineers and technicians with years of experience in solar installations'
    },
    {
      icon: <Award className="w-10 h-10 text-[#0D47A1]" />,
      title: 'Quality Assurance',
      description: 'We use only premium-grade solar products with comprehensive warranties'
    },
    {
      icon: <TrendingUp className="w-10 h-10 text-[#4CAF50]" />,
      title: 'Innovation',
      description: 'Staying ahead with the latest solar technology and sustainable solutions'
    }
  ];

  const stats = [
    { number: '200+', label: 'Successful Installations' },
    { number: '10+', label: 'Cities Covered' },
    { number: '98%', label: 'Customer Satisfaction' },
    { number: '6+', label: 'Years Experience' }
  ];

  return (
    <div className="min-h-screen pt-20">
      {/* Hero Section */}
      <section className="py-20 px-6 bg-gradient-to-br from-[#0D47A1] to-[#1565C0] text-white">
        <div className="max-w-4xl mx-auto text-center">
          <h1 className="text-5xl font-bold mb-6">About Yours Energy Solutions</h1>
          <p className="text-xl text-gray-100 leading-relaxed">
            Leading the way in solar rooftop system solutions across India
          </p>
        </div>
      </section>

      {/* Story Section */}
      <section className="py-20 px-6 bg-white">
        <div className="max-w-6xl mx-auto">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-4xl font-bold text-[#0D47A1] mb-6">Our Story</h2>
              <p className="text-lg text-gray-700 mb-6 leading-relaxed">
                Yours Energy Solutions delivers reliable and efficient solar rooftop systems to power a greener future for homes, schools, and businesses across India. We believe in the transformative power of solar energy to create a sustainable and independent energy future.
              </p>
              <p className="text-lg text-gray-700 mb-6 leading-relaxed">
                Founded with a vision to make clean energy accessible to all, we have grown to become one of India's trusted names in solar solutions. Our end-to-end services cover everything from system design and installation to maintenance and government subsidy support.
              </p>
              <p className="text-lg text-gray-700 leading-relaxed">
                With a team of certified engineers and technicians, we ensure every project meets the highest standards of quality and efficiency. Your energy independence is our commitment.
              </p>
            </div>
            <div className="rounded-lg overflow-hidden shadow-2xl">
              <img 
                src="https://images.unsplash.com/photo-1559302504-64aae6ca6b6d?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzB8MHwxfHNlYXJjaHw0fHxzb2xhciUyMHJvb2Z0b3AlMjBwYW5lbHN8ZW58MHx8fHwxNzYwNDYxMjc2fDA&ixlib=rb-4.1.0&q=85" 
                alt="Team Installing Solar" 
                className="w-full h-full object-cover hover:scale-105 transition-transform duration-500"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-20 px-6 bg-gray-50">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-4xl font-bold text-center text-[#0D47A1] mb-12">Our Impact</h2>
          <div className="grid md:grid-cols-4 gap-8">
            {stats.map((stat, index) => (
              <div key={index} className="text-center">
                <div className="text-5xl font-bold text-[#FFC107] mb-2">{stat.number}</div>
                <div className="text-gray-700 font-medium text-lg">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Values Section */}
      <section className="py-20 px-6 bg-white">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-4xl font-bold text-center text-[#0D47A1] mb-12">Our Values</h2>
          <div className="grid md:grid-cols-2 gap-8">
            {values.map((value, index) => (
              <Card key={index} className="hover:shadow-xl transition-all duration-300 border-none bg-gray-50">
                <CardContent className="p-8">
                  <div className="flex items-start gap-4">
                    <div className="flex-shrink-0">{value.icon}</div>
                    <div>
                      <h3 className="text-2xl font-bold text-[#0D47A1] mb-3">{value.title}</h3>
                      <p className="text-gray-700 leading-relaxed">{value.description}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Team Image Section */}
      <section className="py-20 px-6 bg-gray-50">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-4xl font-bold text-center text-[#0D47A1] mb-12">Our Expert Team</h2>
          <div className="grid md:grid-cols-3 gap-6">
            <div className="rounded-lg overflow-hidden shadow-lg">
              <img 
                src="https://images.unsplash.com/photo-1660330589505-9a433a742a7b?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2NDF8MHwxfHNlYXJjaHwzfHxzb2xhciUyMGluc3RhbGxhdGlvbnxlbnwwfHx8fDE3NjA0NjEyODJ8MA&ixlib=rb-4.1.0&q=85" 
                alt="Team Member" 
                className="w-full h-64 object-cover hover:scale-105 transition-transform duration-500"
              />
            </div>
            <div className="rounded-lg overflow-hidden shadow-lg">
              <img 
                src="https://images.unsplash.com/photo-1542336391-ae2936d8efe4?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2NDF8MHwxfHNlYXJjaHw0fHxzb2xhciUyMGluc3RhbGxhdGlvbnxlbnwwfHx8fDE3NjA0NjEyODJ8MA&ixlib=rb-4.1.0&q=85" 
                alt="Team Member" 
                className="w-full h-64 object-cover hover:scale-105 transition-transform duration-500"
              />
            </div>
            <div className="rounded-lg overflow-hidden shadow-lg">
              <img 
                src="https://images.pexels.com/photos/8853502/pexels-photo-8853502.jpeg" 
                alt="Team Member" 
                className="w-full h-64 object-cover hover:scale-105 transition-transform duration-500"
              />
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default About;